import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import CircledIcon from './CircledIcon';
import ReactHtmlParser from 'react-html-parser';

const Analysis = ({ headline, analysis, skills, score, terms, titles }) => {
  const [decoratedHeadline, setDecoratedHeadline] = useState('');

  const ITEM_DATA = {
    valueProp: {
      title: '"I Help" Pitch',
      color: 'bg-info',
      pass: 'Nice! Your Headline speaks directly to how you help companies drive value',
      fail: `It's not clear how you add value to companies. Try including a pitch that starts with "I Help..." More info below.`,
    },
    title: {
      title: 'Job Title',
      color: 'bg-primary',
      pass: 'Nice work! Your headline includes a job title.',
      fail: 'Your Headline does not include a job title. Add one to let people know about your experience and goals.',
    },
    skills: {
      title: 'Hard & Soft Skills',
      color: 'bg-danger',
      pass: 'Awesome! Your headline has at least 3 hard or soft skills.',
      fail: `Your Headline ${
        skills?.length > 0 ? `has ${skills?.length}` : "doesn't have any"
      } hard or soft skills. We recommend a minimum of 3`,
    },
    metrics: {
      title: 'Measurable Metrics',
      color: 'bg-warning',
      pass: 'Perfect! Your Headline has at least one measurable metric',
      fail: "Your Headline doesn't have any measurable metrics or results. We recommend at least one.",
    },
    headlineLength: {
      title: 'Headline Length',
      color: 'bg-dark',
      pass: 'Your headline is a great length (see below for more info).',
      fail: `Your headline is too ${
        headline?.split(' ')?.length < 10 || headline?.length < 75
          ? 'short'
          : 'long'
      }. See below for more info`,
    },
  };

  useEffect(() => {
    if (headline) {
      analyzeText();
    }
  }, [headline]);

  const analyzeText = () => {
    let text = ` ${headline} `;
    const helpRegex = /(i help)/i;
    const percentRegex = new RegExp(/(\s[$€£¥₣₹]*[0-9.,]+\s*[%kKmM$€£¥₣₹+-x]*\s)/);

    for (let i = 0; i < titles.length; i++) {
      const title = titles[i];
      const idx = text.toLowerCase().indexOf(` ${title} `);
      if (idx > -1) {
        text =
          text.slice(0, idx + 1) +
          '<span class="text-primary">' +
          text.slice(idx + 1, idx + title.length + 1) +
          '</span>' +
          text.slice(title.length + 1 + idx);
      }
    }

    for (let i = 0; i < terms.length; i++) {
      const term = terms[i].potential_term.toLowerCase();
      const idx =
        text.toLowerCase().indexOf(` ${term} `) > -1
          ? text.toLowerCase().indexOf(` ${term} `)
          : text.toLowerCase().indexOf(` ${term},`);
      if (idx > -1) {
        text =
          text.slice(0, idx + 1) +
          '<span class="text-danger">' +
          text.slice(idx + 1, idx + term.length + 1) +
          '</span>' +
          text.slice(term.length + 1 + idx);
      }
    }

    text = text
      .split(helpRegex)
      .map(item => {
        if (helpRegex.test(` ${item} `)) {
          return `<span class="text-info">${item}</span>`;
        }
        return item;
      })
      .join(' ');

    text = text
      .split(' ')
      .map(chunk => {
        if (percentRegex.test(` ${chunk} `)) {
          console.log({ chunk });
          return `<span class="text-warning">${chunk}</span>`;
        }
        return chunk;
      })
      .join(' ');

    setDecoratedHeadline(text.trim());
  };

  const scoreColor = () => {
    if (score > 69) {
      return 'bg-info';
    } else if (score > 45 && score < 70) {
      return 'bg-warning';
    }
    return 'bg-danger';
  };

  return (
    <div className="pt-5">
      <div className="container px-3 px-sm-5">
        <div className="d-flex align-items-center justify-content-center">
          <h1 className="font-weight-bold mb-sm-4 text-center">
            LinkedIn Headline Score
          </h1>
        </div>
        <div className="row px-lg-5 mx-xl-5 border-bottom pb-5 mb-4 d-flex align-items-center">
          <div className="col-12 col-sm-2 d-flex align-items-center mb-3 mt-1 mt-sm-0 mb-sm-0 justify-content-center">
            <div className={`${scoreColor()} square p-3`}>
              <h1 className="text-white header-up">{score}</h1>
            </div>
          </div>
          <div className="col d-flex">
            <h3 className="text-center text-sm-left m-auto mx-sm-0">
              {ReactHtmlParser(decoratedHeadline)}
            </h3>
          </div>
        </div>

        <div className="row my-3">
          <div className="col-12 col-md-5 col-lg-4 col-xl-3">
            <h5 className="font-weight-bold">Headline Anatomy</h5>
          </div>
          <div className="col-6 col-md-2 d-flex justify-content-center">
            <h5 className="font-weight-bold d-none d-md-block">Included?</h5>
          </div>
          <div className="col">
            <h5 className="font-weight-bold d-none d-md-block">Notes</h5>
          </div>
        </div>

        {analysis &&
          Object.keys(analysis)?.map(item => (
            <div key={item} className="row align-items-center mt-3">
              <div className="col-1 justify-content-center align-items-center">
                <div
                  className={`circle rounded-circle ${ITEM_DATA[item].color}`}
                />
              </div>
              <div className="col-6 col-md-3 col-xl-2 d-flex align-items-center">
                <p className="my-auto header-up pl-2">
                  <span className="font-override">{ITEM_DATA[item].title}</span>
                </p>
              </div>
              <div className="col-5 col-md-2 d-flex align-items-center justify-content-center">
                <CircledIcon value={analysis[item]} />
              </div>
              <div className="col-12 col-md-5 col-xl-7 d-flex align-items-center">
                <p className="mt-3 my-md-auto">
                  <span className="font-override">
                    {analysis[item]
                      ? ITEM_DATA[item].pass
                      : ITEM_DATA[item].fail}
                  </span>
                </p>
              </div>
            </div>
          ))}

        <div className="d-flex align-items-center justify-content-center">
          <h3 className="font-weight-bold mt-5 text-center">
            Scroll For Headline Analysis & Recommendations
          </h3>
        </div>
        <div className="d-flex align-items-center justify-content-center">
          <FontAwesomeIcon size="3x" icon={faChevronDown} />
        </div>
      </div>
    </div>
  );
};
export default Analysis;
